import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { alertError } from '../../../store/actions/alert';
import { uploadProductsFile } from '../../../store/actions/productsFeeds';
import { FIFTY_MEGABYTES } from '../../../constants/fileSize';
import { CSV_MIME_TYPE } from '../../../utils/csv';

import ModalWindow from '../../common/Modal';
import { UploadFileHeader, UploadFileForm, UploadFileFooter, UploadFileAttachments } from '../upload-file-components';
import { UPLOAD_PRODUCTS_GOOGLE_SHEET_URL, UPLOAD_PRODUCTS_XLSX_URL } from '../../../constants/productUpload';
import { DownloadReportIcon } from '../../icons/analytics';

import styles from './UploadProductsFeedsModal.module.scss';

export function UploadProductsFeedsModal(props) {
  const { onUpload, isOpen } = props;
  const dispatch = useDispatch();
  const { isSubmitting, hasSubmitted } = useSelector((state) => state.productsFeeds.upload);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileSelect = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== CSV_MIME_TYPE) {
      dispatch(alertError('Please only use csv files'));
      return;
    }

    file && file.size <= FIFTY_MEGABYTES
      ? setSelectedFile(file)
      : dispatch(alertError('File size is larger than 50 MB. Please upload a smaller file.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasSubmitted) {
      onClose();
      setSelectedFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSubmitted]);

  const onClose = () => {
    setSelectedFile(null);
    props.onClose();
  };

  const handleSubmit = () => {
    if (!selectedFile) return;

    dispatch(uploadProductsFile(selectedFile, onUpload));
  };

  return (
    <ModalWindow open={isOpen} onClose={onClose} size="medium" center shouldUseModernStyles={true}>
      <div className={styles.modalContent}>
        <UploadFileHeader title="Upload Products Data" subtitle="" />
        <UploadFileForm selectedFile={selectedFile} handleFileSelect={handleFileSelect} />
        <UploadFileAttachments
          attachments={[
            <a key="example-file" href={UPLOAD_PRODUCTS_GOOGLE_SHEET_URL} target="_blank" rel="noopener noreferrer">
              <span className={styles.icon}>
                <FontAwesomeIcon strokeWidth={0.3} icon={faExternalLinkAlt} />
              </span>{' '}
              Example Google Sheet
            </a>,
            <a key="example-file" href={UPLOAD_PRODUCTS_XLSX_URL} target="_blank" rel="noopener noreferrer">
              <span className={styles.icon}>
                <DownloadReportIcon strokeColor="#337ab7" strokeWidth={1.7} />
              </span>{' '}
              Example Excel File
            </a>,
          ]}
        />
        <UploadFileFooter
          isSubmitInProgress={isSubmitting}
          onClose={onClose}
          disabled={!selectedFile}
          handleUpload={handleSubmit}
        />
      </div>
    </ModalWindow>
  );
}

UploadProductsFeedsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
};
